import { PageProps } from "gatsby";
import React, { FC } from "react";

import { AccordionSection, AccordionSectionProps } from "../strips/accordion-section";
import { Anchor, AnchorProps } from "../strips/anchor";
import {
  AnimatedSliderSection,
  AnimatedSliderSectionProps,
} from "../strips/animated-slider-section";
import { BannerCards, BannerCardsProps } from "../strips/banner-cards";
import { BannerGrid, BannerGridProps } from "../strips/banner-grid";
import { BannerImage, BannerImageProps } from "../strips/banner-image";
import { BannerZip, BannerZipProps } from "../strips/banner-zip";
import { CompanyCalendar, CompanyCalendarProps } from "../strips/company-calendar";
import { EditorialText, EditorialTextProps } from "../strips/editorial-text";
import { EmbeddedContent, EmbeddedContentProps } from "../strips/embedded-content";
import {
  EmphasisSliderSection,
  EmphasisSliderSectionProps,
} from "../strips/emphasis-slider-section";
import { EventsGridSection, EventsGridSectionProps } from "../strips/events-grid-section";
import { FileCategorySection, FileCategorySectionProps } from "../strips/file-category-section";
import { Grid, GridProps } from "../strips/grid";
import { HeroBanner, HeroBannerProps } from "../strips/hero-banner";
import { HeroBannerVariants } from "../strips/hero-banner/types";
import { HeroPortrait, HeroPortraitProps } from "../strips/hero-portrait";
import { HeroSliderSection, HeroSliderSectionProps } from "../strips/hero-slider-section";
import { JobsGridSection, JobsGridSectionProps } from "../strips/jobs-grid-section";
import { MaterialsSlider, MaterialsSliderProps } from "../strips/materials-slider";
import {
  ParallaxBannerSection,
  ParallaxBannerSectionProps,
} from "../strips/parallax-banner-section";
import {
  ProductFinderSection,
  ProductFinderSectionProps,
} from "../strips/product-finder/product-finder-section";
import { SpecialContent, SpecialContentProps } from "../strips/special-content";
import { TableSection, TableSectionProps } from "../strips/table-section";
import { TabsSection, TabsSectionProps } from "../strips/tabs-section";
import {
  VerticalSliderSection,
  VerticalSliderSectionProps,
} from "../strips/vertical-slider-section";
import { WorldMap, WorldMapProps } from "../strips/world-map/world-map";

type RendererProps = {
  location: PageProps["location"];
  index: number;
} & (
  | ({ __typename: "ContentfulHeroSliderSection" } & HeroSliderSectionProps)
  | ({ __typename: "ContentfulMaterialsSlider" } & MaterialsSliderProps)
  | ({ __typename: "ContentfulAnimatedSliderSection" } & AnimatedSliderSectionProps)
  | ({ __typename: "ContentfulVerticalSliderSection" } & VerticalSliderSectionProps)
  | ({ __typename: "ContentfulParallaxBannerSection" } & ParallaxBannerSectionProps)
  | ({ __typename: "ContentfulEditorialText" } & EditorialTextProps)
  | ({ __typename: "ContentfulBannerImage" } & BannerImageProps)
  | ({ __typename: "ContentfulWorldMap" } & WorldMapProps)
  | ({ __typename: "ContentfulHeroBanner" } & Omit<HeroBannerProps, "tagVariant">)
  | ({ __typename: "ContentfulBannerGrid" } & BannerGridProps)
  | ({ __typename: "ContentfulBannerZip" } & BannerZipProps)
  | ({ __typename: "ContentfulBannerCards" } & BannerCardsProps)
  | ({ __typename: "ContentfulAccordionSection" } & AccordionSectionProps)
  | ({ __typename: "ContentfulEmphasisSliderSection" } & EmphasisSliderSectionProps)
  | ({ __typename: "ContentfulGrid" } & GridProps)
  | ({ __typename: "ContentfulSpecialContent" } & SpecialContentProps)
  | ({ __typename: "ContentfulJobsGridSection" } & JobsGridSectionProps)
  | ({ __typename: "ContentfulTableSection" } & TableSectionProps)
  | ({ __typename: "ContentfulTabsSection" } & TabsSectionProps)
  | ({ __typename: "ContentfulCompanyCalendar" } & CompanyCalendarProps)
  | ({ __typename: "ContentfulFileCategory" } & FileCategorySectionProps)
  | ({ __typename: "ContentfulHeroPortrait" } & HeroPortraitProps)
  | ({ __typename: "ContentfulEmbeddedContent" } & EmbeddedContentProps)
  | ({ __typename: "ContentfulAnchor" } & AnchorProps)
  | ({ __typename: "ContentfulProductFinderSection" } & ProductFinderSectionProps)
  | ({ __typename: "ContentfulEventsGridSection" } & EventsGridSectionProps)
);

export const Renderer: FC<RendererProps> = ({ index, location, ...props }) => {
  const { __typename } = props;

  switch (__typename) {
    case "ContentfulHeroSliderSection":
      return <HeroSliderSection {...props} />;
    case "ContentfulMaterialsSlider":
      return <MaterialsSlider {...props} />;
    case "ContentfulAnimatedSliderSection":
      return <AnimatedSliderSection {...props} />;
    case "ContentfulVerticalSliderSection":
      return <VerticalSliderSection {...props} />;
    case "ContentfulParallaxBannerSection":
      return <ParallaxBannerSection {...props} />;
    case "ContentfulEditorialText":
      return <EditorialText {...props} isHero={index === 0} />;
    case "ContentfulBannerImage":
      return <BannerImage {...props} />;
    case "ContentfulWorldMap":
      return <WorldMap {...props} />;
    case "ContentfulHeroBanner":
      return (
        <HeroBanner
          {...props}
          tagVariant={!index ? HeroBannerVariants.h1 : HeroBannerVariants.h2}
        />
      );
    case "ContentfulBannerGrid":
      return <BannerGrid {...props} />;
    case "ContentfulBannerZip":
      return <BannerZip {...props} />;
    case "ContentfulBannerCards":
      return <BannerCards {...props} />;
    case "ContentfulAccordionSection":
      return <AccordionSection {...props} />;
    case "ContentfulEmphasisSliderSection":
      return <EmphasisSliderSection {...props} />;
    case "ContentfulGrid":
      return <Grid {...props} />;
    case "ContentfulSpecialContent":
      return <SpecialContent {...props} location={location} />;
    case "ContentfulJobsGridSection":
      return <JobsGridSection {...props} />;
    case "ContentfulTableSection":
      return <TableSection {...props} />;
    case "ContentfulTabsSection":
      return <TabsSection {...props} location={location} />;
    case "ContentfulCompanyCalendar":
      return <CompanyCalendar {...props} />;
    case "ContentfulFileCategory":
      return <FileCategorySection {...props} />;
    case "ContentfulHeroPortrait":
      return <HeroPortrait {...props} />;
    case "ContentfulEmbeddedContent":
      return <EmbeddedContent {...props} />;
    case "ContentfulAnchor":
      return <Anchor {...props} />;
    case "ContentfulProductFinderSection":
      return <ProductFinderSection {...props} />;
    case "ContentfulEventsGridSection":
      return <EventsGridSection {...props} />;
    default:
      console.error("Unable to render", __typename);
      return null;
  }
};
